import { Core } from "@faire/design-tokens";
import { LoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/LoginError";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { getLocationOrThrow } from "@faire/web--source/common/globals/getLocation";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { parseQueryParams } from "@faire/web--source/common/query-params/parseQueryParams";
import { Button } from "@faire/web--source/slate/Button";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { History } from "history";
import isObject from "lodash/isObject";
import type { JSX } from "react";
import * as React from "react";
import { useHistory } from "react-router-dom";

type ErrorMessageProps = {
  error?: LoginError;
  align?: "left" | "center";
  small?: boolean;
};

export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  error,
  align,
  small,
}) => {
  if (!error || !isObject(error)) {
    return null;
  }

  const serverError =
    error.httpStatusCode !== undefined && error.httpStatusCode >= 500;
  const badTokenError =
    error.httpStatusCode !== undefined &&
    error.httpStatusCode === 400 &&
    error.responseBody?.field === "resetPasswordToken";
  const queryParams = parseQueryParams(getLocationOrThrow().search);
  const isMobile = queryParams.get("native_env") === "true";

  if (serverError) {
    return (
      <>
        <Spacer height="1x" />
        <Typography
          role="alert"
          color={Core.text.critical}
          align={align ?? "center"}
          data-test-id="signin-error-msg"
          variant={small ? "labelSansRegular" : "paragraphSansRegular"}
        >
          <StrictLocalMsg defaultMessage="Something went wrong. Please try again or contact us." />
        </Typography>
      </>
    );
  } else if (badTokenError) {
    return (
      <>
        <Spacer height="1x" />
        <Typography
          role="alert"
          color={Core.text.critical}
          align={align ?? "center"}
          data-test-id="signin-error-msg"
          variant={small ? "labelSansRegular" : "paragraphSansRegular"}
        >
          <StrictLocalMsg
            defaultMessage="This reset password link has expired or already been used."
            description={{
              text: "Error message that reset link is expired.",
              img: "https://cdn.faire.com/fastly/3813a5f893414e0519067ed84ccb9190deca66cce719fddbbd2e3f9c20cc9f7b.png",
            }}
          />
        </Typography>
        {!isMobile ? (
          <Button
            variant="tertiary"
            onClick={() =>
              getLocationOrThrow().replace(
                getCurrentPath().replace({ signIn: "1" }).toString()
              )
            }
          >
            <StrictLocalMsg
              defaultMessage="Login with your new password"
              description={{
                text: "CTA to log in with new password.",
                img: "https://cdn.faire.com/fastly/bbad9032948b3b3757baf8d31a60c75f93983acc13cb434b0c2df255bab44131.png",
              }}
            />
          </Button>
        ) : null}
      </>
    );
  }

  return (
    <>
      <Spacer height="1x" />
      <Typography
        role="alert"
        color={Core.text.critical}
        align={align ?? "center"}
        data-test-id="signin-error-msg"
        variant={small ? "labelSansRegular" : "paragraphSansRegular"}
      >
        {error.responseBody?.message ?? error.message}
      </Typography>
    </>
  );
};

export const ErrorNotValidEmail: React.FC<{
  error: LoginError;
  email: string | undefined;
}> = ({ error, email }) => {
  const history = useHistory();
  if (error.httpStatusCode === 404 && email) {
    return (
      <>
        <Spacer height="1x" />
        <Typography
          role="alert"
          color={Core.text.critical}
          align="left"
          data-test-id="signin-error-msg"
          variant="labelSansRegular"
        >
          <StrictLocalMsg
            defaultMessage="No account found for this email. <EmailLinkFormatting>Sign up</EmailLinkFormatting>"
            description={{
              text: "Let the user know that this email is signed up with Faire and give them the link to sign up",
            }}
            values={{
              EmailLinkFormatting: getCreateAccountLink(history, email),
            }}
          />
        </Typography>
      </>
    );
  } else {
    return <ErrorMessage error={error} align="left" small />;
  }
};

const getCreateAccountLink = (history: History, signUpEmail: string) =>
  function (chunks: JSX.Element) {
    return (
      <Button
        tertiaryColor="inherit"
        small
        onClick={() => {
          popSignUp(
            history,
            SignUpType.GENERIC,
            () => {},
            {
              element: SignUpElement.MODAL,
              sourceId: "signin_modal_email_view_create_account_link_new_UI",
            },
            {
              email: signUpEmail,
              signIn: undefined,
            }
          );
        }}
        variant="tertiary"
      >
        <Typography variant="labelSansRegular">{chunks}</Typography>
      </Button>
    );
  };
